<!--
@component
MenuMatchesRail

Thin wrapper around `MatchesWidget` used in the global header. It owns
the data side of things — pulling contested matches from `appState`
and filtering down to live + soonest-upcoming — so `Menu` itself stays
focused on layout / navigation concerns.

The number of cards on screen at any given viewport is decided by
`MatchesWidget` itself (its built-in responsive 1 / 2 / 3 behaviour).
This wrapper deliberately does *not* maintain its own responsive cap
anymore: doing so meant two layers were independently computing a count,
and during initial loading the placeholder rail would render the
wrapper's count (often the desktop default `3`) before snapping down
to the count the widget actually wanted. Trusting the widget as the
single source of truth keeps the placeholder count and the real-card
count in sync from the very first paint.

We still pre-cap the matches passed in to a small ceiling (`3`, the
widest viewport's needs) so we're not handing the widget the entire
schedule and asking it to slice — `getLiveAndUpcomingMatches` does the
expensive sort/filter, so capping upstream avoids extra work.
-->

<script lang="ts">
  import NextMatches from '$lib/components/common/MatchesWidget/index.svelte';
  import { appState } from '$lib/stores/index.svelte';
  import getContestedMatches from '$utils/getContestedMatches';
  import { getLiveAndUpcomingMatches } from '$utils/getNextMatches';
  import type { ContestedMatch } from '$lib/@types/opta';

  /**
   * Max number of live/upcoming matches we ever surface in the header
   * rail at any viewport. The widget itself trims further (down to 1
   * or 2) on narrower viewports, but capping here means we never
   * process more matches than the widest viewport could possibly show.
   */
  const HEADER_RAIL_MAX = 4;

  /** Live + soonest-upcoming matches across the whole tournament. */
  const matches = $derived<ContestedMatch[]>(
    getLiveAndUpcomingMatches(
      getContestedMatches(appState.optaData),
      HEADER_RAIL_MAX
    )
  );
</script>

<NextMatches {matches} />
